import { ReactNode } from "react";
import { Box, Dialog, DialogContent, Typography, alpha } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { Button } from "./Button";

const NOTA_TEXT = "#5C6B7C";
const DIALOG_SCRIM = "rgba(6, 44, 78, 0.55)";
// `.exp-modal-backdrop` / `.exp-modal` del diseño v2 (layout `sheet`).
const SHEET_SCRIM = "rgba(0, 38, 82, 0.32)";
const SHEET_SHADOW = "0 20px 60px rgba(0, 38, 82, 0.25)";

export type DialogConfirmTone = "primary" | "danger" | "success";

/**
 * `centered` (default): contenido centrado y botones a lo ancho. `sheet`: el `.exp-modal` del diseño (título con
 * línea abajo, texto alineado a la izquierda y botones a la derecha: secundario como texto y principal con flecha).
 */
export type DialogConfirmLayout = "centered" | "sheet";

interface Props {
  open: boolean;
  title: string;
  /** Texto principal; acepta nodos para resaltar partes con `<strong>`. */
  message: ReactNode;
  /** Ícono dentro del círculo de color de `tone`. Sin ícono no se dibuja el círculo. */
  icon?: ReactNode;
  /** `danger` pinta el ícono en rojo y el botón de confirmar en oscuro (acción destructiva). */
  tone?: DialogConfirmTone;
  confirmLabel: string;
  /** Sin `cancelLabel` el modal solo tiene el botón de confirmar (avisos de resultado). */
  cancelLabel?: string;
  /** Texto del botón de confirmar mientras `loading`. */
  loadingLabel?: string;
  /** Mientras carga: botones deshabilitados y el modal no se puede cerrar. */
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Contenido extra entre el mensaje y los botones (p. ej. una comparación de montos). */
  children?: ReactNode;
  /** Opt-in: `sheet` para los modales que en el diseño usan `.exp-modal` (p. ej. confirmar el monto en `UsoLinea`). */
  layout?: DialogConfirmLayout;
  confirmTestId: string;
  cancelTestId?: string;
}

/**
 * Modal de confirmación / aviso del wizard v2. Mismo spec que los diálogos de ofertas
 * (`DialogDescartarOferta`): scrim azul con blur, radio 18, contenido centrado y botones a lo ancho.
 * Cada pantalla le pasa sus textos en lugar de tener su propio componente de modal.
 */
export const DialogConfirm = (props: Props) =>
  props.layout === "sheet" ? <DialogSheet {...props} /> : <DialogCentered {...props} />;

const DialogCentered = ({
  open,
  title,
  message,
  icon,
  tone = "primary",
  confirmLabel,
  cancelLabel,
  loadingLabel,
  loading = false,
  onConfirm,
  onClose,
  children,
  confirmTestId,
  cancelTestId,
}: Props) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    fullWidth
    PaperProps={{ sx: { maxWidth: 440, m: 2, borderRadius: "18px", boxShadow: "0 24px 64px rgba(6, 44, 78, 0.35)" } }}
    slotProps={{ backdrop: { sx: { bgcolor: DIALOG_SCRIM, backdropFilter: "blur(4px)" } } }}
  >
    <DialogContent sx={{ p: "30px 28px", textAlign: "center" }}>
      {icon && (
        <Box
          sx={{
            width: 48,
            height: 48,
            mx: "auto",
            mb: "16px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: tone === "danger" ? "error.main" : tone === "success" ? "primary.accent" : "primary.main",
            bgcolor: (theme) =>
              alpha(
                tone === "danger"
                  ? theme.palette.error.main
                  : tone === "success"
                    ? theme.palette.primary.accent
                    : theme.palette.primary.main,
                0.12,
              ),
          }}
        >
          {icon}
        </Box>
      )}

      <Typography sx={{ fontWeight: 600, fontSize: 20, color: "primary.dark", mb: "10px" }}>{title}</Typography>
      <Typography
        component="div"
        sx={{ fontSize: 14.5, lineHeight: 1.55, color: NOTA_TEXT, mb: "24px", "& strong": { color: "primary.dark" } }}
      >
        {message}
      </Typography>

      {children && <Box sx={{ mb: "24px" }}>{children}</Box>}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        {cancelLabel && (
          <Button tone="outline" fullWidth disabled={loading} onClick={onClose} data-testid={cancelTestId}>
            {cancelLabel}
          </Button>
        )}
        <Button
          tone={tone === "danger" ? "dark" : "primary"}
          fullWidth
          disabled={loading}
          onClick={onConfirm}
          data-testid={confirmTestId}
        >
          {loading && loadingLabel ? loadingLabel : confirmLabel}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

/**
 * `.exp-modal` del diseño v2: 460px, radio 16, título de 19px con línea abajo, texto de 13.5px a la izquierda y pie
 * con los botones a la derecha. El `icon` y el `tone` no aplican: el diseño no los usa en este formato.
 */
const DialogSheet = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loadingLabel,
  loading = false,
  onConfirm,
  onClose,
  children,
  confirmTestId,
  cancelTestId,
}: Props) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    fullWidth
    PaperProps={{ sx: { maxWidth: 460, m: 2, borderRadius: "16px", boxShadow: SHEET_SHADOW } }}
    slotProps={{ backdrop: { sx: { bgcolor: SHEET_SCRIM, backdropFilter: "blur(2px)" } } }}
  >
    {/* `.exp-modal-head` */}
    <Box sx={{ p: "20px 24px 16px", borderBottom: "1px solid", borderBottomColor: "tokens.borderStrong" }}>
      <Typography
        component="h3"
        sx={{ fontSize: 19, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.01em", color: "text.primary" }}
      >
        {title}
      </Typography>
    </Box>

    {/* `Typography` y no `Box`: sin CssBaseline, un `Box` toma la fuente del navegador y no la del tema. */}
    <Typography
      component="div"
      sx={{
        p: "4px 22px 0",
        mt: "12px",
        fontSize: 13.5,
        lineHeight: 1.6,
        color: "text.secondary",
        "& strong": { color: "text.primary", fontWeight: 700 },
      }}
    >
      {message}
      {children && <Box sx={{ mt: "14px" }}>{children}</Box>}
    </Typography>

    {/* `.actions` del modal: línea arriba y botones a la derecha */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        mt: "24px",
        p: "18px 22px 20px",
        borderTop: "1px solid",
        borderTopColor: "divider",
      }}
    >
      {cancelLabel && (
        <Button
          tone="ghost"
          disabled={loading}
          onClick={onClose}
          data-testid={cancelTestId}
          sx={{
            height: 34,
            px: "12px",
            fontSize: 13,
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { bgcolor: "tokens.surface2", color: "text.primary" },
          }}
        >
          {cancelLabel}
        </Button>
      )}
      <Button
        disabled={loading}
        onClick={onConfirm}
        data-testid={confirmTestId}
        endIcon={<ArrowForward sx={{ fontSize: "14px !important" }} />}
        sx={{ height: 42, px: "18px", fontSize: 14, fontWeight: 500 }}
      >
        {loading && loadingLabel ? loadingLabel : confirmLabel}
      </Button>
    </Box>
  </Dialog>
);
